import { useTranslation } from 'react-i18next';
import PageHeader from './PageHeader';

/**
 * Shared "this page could not load its data" state (2026-10-14, lot 3a).
 *
 * WHY IT EXISTS — an API that fails used to leave a BLANK PAGE on the 16 pages
 * that read data without any guard: they called `apiXxx()` inside a `useEffect`
 * and, when the promise rejected, `loading` flipped to false while the data
 * stayed empty, so the reader got a silent void with no message at all. That is
 * the worst possible outcome during a show: the user cannot tell "there is
 * nothing" from "the request failed".
 *
 * SCOPE — this is a LOAD guard, not an error boundary: it renders when the
 * page's own fetch failed and the page therefore has nothing real to show. It
 * never replaces a page that already handles its failure its own way (the pages
 * that own a message keep it — see the reference), and it never touches the
 * requests, the data or any business rule: it RENDERS a state.
 *
 * TEXTS — one tiny shared key set (`pageLoadError.*`, EN/FR/ES). The page
 * supplies its already-translated TITLE; the message and the retry label are
 * shared, so 16 pages never grow 16 different messages.
 */
export interface PageLoadErrorProps {
  /** The page title, already translated by the caller (same string the header shows). */
  title: string;
  /** Optional extra context (the failing scope, a raw API message). Shown as-is. */
  detail?: string | null;
  /**
   * Retry handler — the page re-runs its own load function. Never a bare
   * `location.reload()`: re-running the fetch keeps the SPA state (scroll,
   * filters, open tab) and does not depend on the browser cache.
   */
  onRetry?: () => void;
}

export default function PageLoadError({ title, detail, onRetry }: PageLoadErrorProps) {
  const { t } = useTranslation();

  return (
    <div className="page" data-page-load-error>
      <PageHeader title={title} />
      <div className="card" data-page-load-error-card>
        <strong style={{ color: 'var(--danger)' }}>{t('pageLoadError.title')}</strong>
        <p style={{ color: 'var(--text-dim)', margin: '6px 0 0', fontSize: '0.9rem' }}>
          {t('pageLoadError.message')}
        </p>
        {detail ? (
          <p style={{ color: 'var(--text-dim)', margin: '8px 0 0', fontSize: '0.8rem' }} data-page-load-error-detail>
            {detail}
          </p>
        ) : null}
        {onRetry && (
          <button
            type="button"
            className="btn btn-primary"
            data-page-load-error-retry
            onClick={onRetry}
            style={{ marginTop: 14 }}
          >
            {t('pageLoadError.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The shared WORDING, for the pages that already own an error surface (a hero
 * block, the full-screen dark Show Time view) and must not grow a second
 * message: one key, one meaning, reused instead of re-invented at the call
 * site. `usePageLoadErrorText()` is the hook form (call sites are components);
 * `pageLoadErrorText(t)` is the pure form for anything outside React.
 */
export function usePageLoadErrorText(): string {
  const { t } = useTranslation();
  return t('pageLoadError.message');
}

export function pageLoadErrorText(t: (key: string) => string): string {
  return t('pageLoadError.message');
}
